import { useCallback, useState } from "react"
import { format } from "date-fns"
import {
	IconTrash2Outline18 as Trash2Icon,
	IconArrowTurnLeftOutline18 as UndoIcon,
	IconCircleUserOutline18 as UserCircle2Icon,
} from "nucleo-ui-outline-18"

import type { FeedbackState } from "@productlane/db/browser"
import { Button, Dialog, IconButton } from "@productlane/ui"

import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useImportPortalId } from "../../import-portal"

interface PreviewDialogProps {
	isOpen: boolean
	onOpenChange: () => void
	notes: Note[]
	onSuccess: () => void
}

export interface Note {
	text: string
	contactName?: string
	contactEmail?: string
	createdAt?: string
	state?: FeedbackState
}

export function PreviewDialog({
	isOpen,
	onOpenChange,
	notes,
	onSuccess,
}: PreviewDialogProps) {
	const [deleted, setDeleted] = useState<number[]>([])
	const portalInstanceId = useImportPortalId()

	const { isLoading, mutate } = api.workspace.bulkCreateInsights.useMutation({
		onSuccess,
	})

	const handleDelete = useCallback(
		(i: number) => {
			if (deleted.includes(i)) {
				const newDeleted = deleted.filter((it) => it !== i)
				setDeleted(newDeleted)
			} else {
				setDeleted([...deleted, i])
			}
		},
		[deleted],
	)

	const handleUpload = useCallback(() => {
		const uploadNotes = notes.filter((_, i) => !deleted.includes(i))
		analytics.track("upload-csv")
		mutate({
			notes: uploadNotes,
			portalInstanceId,
		})
	}, [notes, deleted, mutate, portalInstanceId])

	return (
		<Dialog
			title="Preview"
			description="Please remove conversations you might not want to upload"
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			footer={
				<div>
					<Button isLoading={isLoading} onClick={handleUpload}>
						Upload
					</Button>
				</div>
			}
		>
			<div className="space-y-2">
				{notes.map((it, i) => (
					<div
						key={`note-preview-${i}`}
						className="relative flex flex-col overflow-visible rounded-lg border p-2.5 text-sm text-secondary"
					>
						<div className="flex items-center gap-2 border-b">
							<UserCircle2Icon className="h-4 w-4" />
							{it.contactEmail && (
								<>
									{it.contactName}
									{`<${it.contactEmail}>`}
								</>
							)}
							{!it.contactEmail && "No contact"}
							{it.createdAt && (
								<span className="text-small ml-4">
									{format(new Date(it.createdAt), "MMM d, yyyy")}
								</span>
							)}
							<IconButton
								className="z-10 ml-auto"
								onClick={() => handleDelete(i)}
								variant="ghost"
								Icon={deleted.includes(i) ? UndoIcon : Trash2Icon}
							/>
						</div>
						<div className="mt-2">{it.text}</div>

						{deleted.includes(i) && (
							<div className="absolute inset-0 z-1 bg-white/70 dark:bg-gray-900/70" />
						)}
					</div>
				))}
			</div>
		</Dialog>
	)
}
